import React, { Component } from 'react';
import { TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import SingleDateSelectedValue from './SingleDateSelectedValue';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import { dateInputSharedProps } from './helpers/common';
import { Themes } from '@adsk/bim360-matrix-react-helpers/es/theme';
import calendarSvg from '@adsk/bim360-matrix-react-assets/calendar.svg';
import alloyCalendarSvg from '@adsk/bim360-matrix-react-assets/alloy-calendar.svg';

/**
 * DateSelectorInput
 */
class DateSelectorInput extends Component {
  constructor(props) {
    super(props);
    this.state = this.setPlaceholders(props);

    this.clickHandler = this.clickHandler.bind(this);
    this._renderValue = this._renderValue.bind(this);
    this._hasValue = this._hasValue.bind(this);
    this._renderIcon = this._renderIcon.bind(this);
    this.setPlaceholders = this.setPlaceholders.bind(this);
  }

  clickHandler() {
    if (this.props.disabled) {
      return;
    }
    this.props.onClick();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.placeholder !== this.props.placeholder ||
      nextProps.closedStatePlaceholder !== this.props.closedStatePlaceholder ||
      nextProps.locale !== this.props.locale
    ) {
      this.setState({
        ...this.setPlaceholders(nextProps)
      });
    }
  }

  /**
   * set the input placeholders from the props
   */

  setPlaceholders(props) {
    return {
      closedStatePlaceholder:
        props.closedStatePlaceholder || props[TSymbolProp].translate('CHOOSE_DATE'),
      placeholder:
        props.placeholder ||
        (props.range
          ? { start: props[TSymbolProp].translate('FROM'), end: props[TSymbolProp].translate('TO') }
          : props[TSymbolProp].translate('SELECT'))
    };
  }

  /**
   *
   * @returns true if there's a value in the input, false otherwise
   * @private
   */
  _hasValue() {
    const { range, selectedValue, startDate } = this.props;
    return !!(range ? startDate : selectedValue);
  }

  /**
   * render the inner value area of the input
   * @returns {*}
   * @private
   */
  _renderValue() {
    const { range, selectedValue, startDate, endDate, format, open } = this.props;
    const { placeholder, closedStatePlaceholder } = this.state;
    if (!this._hasValue() && !open) {
      return closedStatePlaceholder;
    }
    if (range) {
      return (
        <div>
          <SingleDateSelectedValue
            value={startDate}
            placeholder={placeholder.start}
            format={format}
          />
          {' - '}
          <SingleDateSelectedValue value={endDate} placeholder={placeholder.end} format={format} />
        </div>
      );
    } else {
      return (
        <SingleDateSelectedValue value={selectedValue} placeholder={placeholder} format={format} />
      );
    }
  }

  _renderIcon() {
    const { theme } = this.props;
    if (this.props.noIcon) {
      return null;
    }

    const iconClasses = classNames('DateSelectorInput__icon', {
      'DateSelectorInput__icon--icon-only': this.props.iconOnly,
      'DateSelectorInput__icon--underlined': this.props.underlined,
      'DateSelectorInput__icon--disabled': this.props.disabled
    });

    return (
      <SvgIcon className={iconClasses} transform="translate(0 0.5)">
        {theme === Themes.ALLOY ? alloyCalendarSvg : calendarSvg}
      </SvgIcon>
    );
  }

  render() {
    const { style, className, open, iconOnly, underlined, noIcon, disabled, valid } = this.props;
    const inputClasses = classNames('DateSelectorInput', className);

    const inputContainerClasses = classNames('DateSelectorInput__container', {
      'DateSelectorInput__container--open': open,
      'DateSelectorInput__container--icon-only': iconOnly,
      'DateSelectorInput__container--underlined': underlined,
      'DateSelectorInput__container--with-icon': noIcon,
      'DateSelectorInput__container--disabled': disabled,
      'DateSelectorInput__container--is-open-underline': underlined && open,
      'DateSelectorInput__container--invalid': !valid
    });

    return (
      <div
        className={inputContainerClasses}
        tabIndex="0"
        role="combobox"
        onClick={this.clickHandler}
        data-testid="DateSelector__input-container"
      >
        {this._renderIcon()}
        {!iconOnly && (
          <div className={inputClasses} style={style}>
            {this._renderValue()}
          </div>
        )}
      </div>
    );
  }
}

DateSelectorInput.propTypes = {
  ...dateInputSharedProps,
  /**
   * A boolean to determine wether or not to show the date picker icon
   */
  noIcon: PropTypes.bool,
  /**
   * Use an input with underline style
   */
  underlined: PropTypes.bool,
  /**
   * Shows only date picker icon instead of the whole field
   */
  iconOnly: PropTypes.bool,
  /**
   * A placeholder text when no date is selected
   */
  placeholder: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      start: PropTypes.string.isRequired,
      end: PropTypes.string.isRequired
    })
  ]),
  /**
   * a placeholder text for date before the field was focused
   */
  closedStatePlaceholder: PropTypes.string,
  /**
   * Flag indicating whether or not the input is in a valid or invalid state.
   */
  valid: PropTypes.bool,
  /**
   * Boolean for disabling the component
   */
  disabled: PropTypes.bool,
  /**
   * optional style object for input
   */
  style: PropTypes.object,
  /**
   * optional additional class for the input
   */
  className: PropTypes.string,
  /**
   * whether the selector is open
   */
  open: PropTypes.bool,
  /**
   * callback when the input is clicked
   */
  onClick: PropTypes.func.isRequired,
  /**
   * Date format. Should be a [moment.js](https://momentjs.com/docs/#/parsing/string-format/) string format
   */
  format: PropTypes.string,
  /** theme passed from DateSelector */
  theme: PropTypes.oneOf(Object.values(Themes))
};

DateSelectorInput.defaultProps = {
  noIcon: false,
  iconOnly: false,
  disabled: false,
  format: 'MMM DD, YYYY',
  valid: true
};

export default DateSelectorInput;
